"use client";
import { useTranslations } from "@zapier/i18n";

import { useState, useMemo } from "react";
import {
  convertToAssetId,
  useSupportedAssets,
  useUserAccountRole,
  useSplitTreatments,
  useZapApprovals,
  Zap,
} from "@zapier/client";
import {
  ApprovalType,
  FOLDER_TRASH_ID,
  DEFAULT_FOLDER_ID,
  AssetTypes,
} from "@zapier/utils";
import { Asset, HydratedZapAsset } from "@zapier/bff";
import { ZapModal } from "./types";
import { useBetaDataTableContext as useTableContext } from "@zapier/design-system";
import { ZapRenameModal } from "./ZapRenameModal/ZapRenameModal";
import { ZapChangeOwnerModal } from "./ZapChangeOwnerModal/ZapChangeOwnerModal";
import { MoveToSharedFolderModal } from "../folders/MoveToSharedFolderModal/MoveToSharedFolderModal";
import { MoveZapsModal } from "../folders/MoveModals/MoveZapsModal";
import { ZapDeleteModal } from "./ZapDeleteModal/ZapDeleteModal";
import { AssetDeleteModal } from "../folders/AssetDeleteModal/AssetDeleteModal";
import { CancelApprovalModal } from "../approvals/CancelApprovalModal/CancelApprovalModal";
import { RequestApprovalModal } from "../approvals/RequestApprovalModal/RequestApprovalModal";
import { ZapExportModal } from "./ZapExportModal/ZapExportModal";
import { ZapDuplicateModal } from "./ZapDuplicateModal/ZapDuplicateModal";
import { MoveAssetModal } from "../folders/MoveModals/MoveAssetModal";
interface FolderAttributes {
  selectedFolderId: string;
  selectedFolderName: string;
}

interface Props {
  approvalType?: ApprovalType | undefined;
  clearZapSelection?: () => void; // optional callback to clear the Zap selection in the table
  currentAsset?: Asset;
  currentModal: ZapModal | undefined;
  currentZaps: Zap[] | HydratedZapAsset[] | undefined; // fully hydrated Zaps in scope
  projectId?: string | null;
  setApprovalType?: (approvalType: ApprovalType) => void;
  setCurrentModal: (modal?: ZapModal | undefined) => void;
  setCurrentZap?: (zap: Zap | undefined) => void;
  clearSelection?: () => void;
  closeModalAndClearParameters?: () => void;
  folderId?: string; // used for cache invalidation
}

export function ZapModals({
  approvalType,
  clearZapSelection,
  currentModal,
  currentZaps,
  projectId: _projectId,
  setApprovalType,
  setCurrentModal,
  setCurrentZap,
  closeModalAndClearParameters,
  folderId,
}: Props) {
  const t = useTranslations("zap-modals");

  const { clearSelection } = useTableContext();
  const [movingIntoFolder, setMovingIntoFolder] = useState<FolderAttributes>({
    selectedFolderId: DEFAULT_FOLDER_ID,
    selectedFolderName: "",
  });

  const isBulk = currentZaps && currentZaps?.length > 1;
  const currentZap = currentZaps?.[0];
  const projectId = _projectId || currentZap?.projectId || undefined;

  const { isMPFEnabled } = useSupportedAssets();
  const {
    isZapAndCanvasUnifiedTrashEnabled,
    isPrivateFolderOwnerPolicyEnabled,
  } = useSplitTreatments();
  const { customuserId } = useUserAccountRole();
  const {
    pendingDeleteApproval,
    pendingEnableOrDisableApproval,
    pendingPublishApproval,
    isApprovalLoading,
  } = useZapApprovals({
    zapId: currentZap?.id,
    isLoadingForApprovalModal: currentModal === ZapModal.Approval,
  });

  const closeModal = () => {
    if (closeModalAndClearParameters) {
      closeModalAndClearParameters();
    } else {
      setCurrentZap?.(undefined);
      clearZapSelection?.();
      setCurrentModal();
    }
  };

  // Bulk Zaps
  const enabledZapIds = useMemo(() => {
    return currentZaps?.filter((zap) => zap.isEnabled)?.map((zap) => zap.id);
  }, [currentZaps]);

  // Single Zap
  const zapId = currentZap?.id;
  const zapTitle = currentZap?.title || "";
  const zapOwnerId = currentZap?.customuserId;
  const zapFolder = currentZap?.folder;
  const zapFolderId = currentZap?.folderId || DEFAULT_FOLDER_ID;
  const zapDeletedAt = currentZap?.deletedAt;
  const zapIsEnabled = currentZap?.isEnabled || false;
  const zapIsPublished = Boolean(currentZap?.currentVersion) || false;

  const assetIds = useMemo(() => {
    return isBulk
      ? currentZaps?.map((zap) => convertToAssetId(AssetTypes.Zap, zap.id))
      : [convertToAssetId(AssetTypes.Zap, zapId)];
  }, [currentZaps, isBulk, zapId]);

  const isZapPurgeEligible =
    folderId === FOLDER_TRASH_ID && isZapAndCanvasUnifiedTrashEnabled;

  return (
    <>
      <ZapRenameModal
        id={zapId}
        initialValue={zapTitle}
        isOpen={currentModal === ZapModal.Rename}
        setIsOpen={closeModal}
        folderId={folderId}
      />
      <ZapChangeOwnerModal
        zaps={
          (currentZaps || currentZap
            ? currentZaps || [currentZap]
            : []) as Zap[]
        }
        currentOwnerId={currentZaps?.[0]?.customuserId || zapOwnerId || 0}
        isOpen={currentModal === ZapModal.ChangeOwner}
        setIsOpen={closeModal}
        onSettled={() => {
          clearZapSelection?.();
          clearSelection();
        }}
        folderId={folderId}
      />

      {/** MPF move zap modal */}
      <MoveAssetModal
        assetType={AssetTypes.Zap}
        assetIds={assetIds}
        assetTitle={
          isBulk
            ? t("titles.asset-display", { count: currentZaps?.length || 0 })
            : zapTitle
        }
        isAssetOwnedByUser={
          isBulk
            ? currentZaps?.every((zap) => zap?.owner?.id === customuserId)
            : currentZap?.owner?.id === customuserId ||
              zapOwnerId === customuserId
        }
        isOpen={currentModal === ZapModal.Move && isMPFEnabled}
        setIsOpen={closeModal}
        assetParentId={folderId}
        confirmsOwnerTransfer={isPrivateFolderOwnerPolicyEnabled}
      />

      {/** Deprecated non-MPF move zap modal */}
      <MoveZapsModal
        onSettled={() => {
          clearZapSelection?.();
          clearSelection();
        }}
        currentFolderId={zapDeletedAt ? FOLDER_TRASH_ID : zapFolderId}
        assetIds={
          isBulk ? currentZaps?.map((zap) => String(zap.id)) : [String(zapId)]
        }
        assetsAreShared={zapFolder?.isShared} // The selected zap is in a shared folder
        assetParentId={zapDeletedAt ? FOLDER_TRASH_ID : zapFolderId} // Set the parent folder id to -1 if the zap is in the trash or to 0 if it is null (My Zaps Home Folder)
        assetTitle={
          isBulk
            ? t("titles.asset-display", { count: currentZaps?.length || 0 })
            : zapTitle
        }
        isOpen={currentModal === ZapModal.Move && !isMPFEnabled}
        setIsOpen={closeModal}
        isAssetOwnedByUser={
          isBulk
            ? currentZaps?.every((zap) => zap?.owner?.id === customuserId)
            : currentZap?.owner?.id === customuserId ||
              zapOwnerId === customuserId
        }
        onShowConfirmMoveToTrash={() => setCurrentModal(ZapModal.Delete)}
        onShowConfirmMoveToSharedFolder={({
          selectedFolderId,
          selectedFolderName,
        }: FolderAttributes) => {
          setMovingIntoFolder({
            selectedFolderId,
            selectedFolderName,
          });
          setCurrentModal(ZapModal.MoveToSharedFolderConfirmation);
        }}
      />
      <MoveToSharedFolderModal
        currentFolderId={zapDeletedAt ? FOLDER_TRASH_ID : zapFolderId}
        assetIds={
          isBulk ? currentZaps?.map((zap) => String(zap.id)) : [String(zapId)]
        }
        assetTitle={
          isBulk
            ? t("titles.asset-display", { count: currentZaps?.length || 0 })
            : zapTitle
        }
        isOpen={currentModal === ZapModal.MoveToSharedFolderConfirmation}
        selectedFolderId={movingIntoFolder.selectedFolderId}
        selectedFolderName={movingIntoFolder.selectedFolderName}
        setIsOpen={closeModal}
        assetCount={currentZaps?.length || 1}
        isAssetOwnedByUser={
          isBulk
            ? currentZaps?.every((zap) => zap?.owner?.id === customuserId)
            : currentZap?.owner?.id === customuserId ||
              zapOwnerId === customuserId
        }
        onSettled={() => {
          clearZapSelection?.();
          clearSelection();
        }}
      />
      {isZapPurgeEligible ? (
        <AssetDeleteModal
          selectedAssetIds={assetIds ?? []}
          isOpen={currentModal === ZapModal.Delete}
          setIsOpen={closeModal}
          onSettled={() => {
            clearZapSelection?.();
            clearSelection();
          }}
          folderId={folderId}
          singleAssetName={isBulk ? undefined : zapTitle || undefined}
        />
      ) : (
        <ZapDeleteModal
          currentItemFolderId={zapFolderId || FOLDER_TRASH_ID}
          currentItemIds={
            isBulk
              ? currentZaps?.map((zap) => String(zap.id))
              : zapId
                ? [String(zapId)]
                : []
          }
          currentItemTitle={zapTitle}
          isOpen={currentModal === ZapModal.Delete}
          isZapEnabled={isBulk ? !!enabledZapIds?.length : zapIsEnabled}
          isZapPublished={isBulk ? !!enabledZapIds?.length : zapIsPublished}
          enabledZapIds={
            isBulk ? enabledZapIds : zapIsEnabled && zapId ? [zapId] : undefined
          }
          setApprovalType={setApprovalType}
          setCurrentModal={setCurrentModal}
          setIsOpen={closeModal}
          onSettled={() => {
            clearZapSelection?.();
            clearSelection();
          }}
          projectId={projectId}
          folderId={folderId}
        />
      )}
      <CancelApprovalModal
        isOpen={
          (currentModal === ZapModal.Approval &&
            approvalType == ApprovalType.Delete &&
            !!pendingDeleteApproval) ||
          (currentModal === ZapModal.Approval &&
            (approvalType == ApprovalType.TurnOn ||
              approvalType === ApprovalType.TurnOff) &&
            !!pendingEnableOrDisableApproval) ||
          (currentModal === ZapModal.Approval &&
            approvalType === ApprovalType.Publish &&
            !!pendingPublishApproval)
        }
        setIsOpen={closeModal}
        type={approvalType}
        zapId={zapId}
      />
      <RequestApprovalModal
        isOpen={
          !isApprovalLoading &&
          currentModal === ZapModal.Approval &&
          ((approvalType === ApprovalType.Delete && !pendingDeleteApproval) ||
            ((approvalType === ApprovalType.TurnOn ||
              approvalType === ApprovalType.TurnOff) &&
              !pendingEnableOrDisableApproval))
        }
        setIsOpen={closeModal}
        type={approvalType}
        zapId={zapId}
        title={zapTitle}
      />
      <ZapExportModal
        zaps={
          (currentZaps || currentZap
            ? currentZaps || [currentZap]
            : []) as Zap[]
        }
        isOpen={currentModal === ZapModal.Export}
        setIsOpen={closeModal}
        onSettled={clearZapSelection}
      />
      <ZapDuplicateModal
        currentItemTitle={zapTitle}
        currentItemId={zapId}
        isOpen={currentModal === ZapModal.Duplicate}
        projectId={projectId}
        setIsOpen={closeModal}
        setCurrentModal={setCurrentModal}
        folderId={folderId}
      />
    </>
  );
}
